/**
 * All Agreements Section (HR Dashboard)
 *
 * Displays all open agreements across the organization.
 * HR can view and edit all agreements.
 */

import { useState } from 'react';
import { useAllOrganizationAgreements } from './useAgreements';
import { AgreementDetailModal } from './AgreementDetailModal';
import type { ScoreAgreement } from '../../lib/evaluationsApi';
import { matchesSearch } from '../../lib/textUtils';

const STATUS_LABELS: Record<string, { label: string; color: string; bgColor: string }> = {
  concept: { label: 'Concept', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  definitief: { label: 'Definitief', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  afgerond: { label: 'Afgerond', color: 'text-green-700', bgColor: 'bg-green-100' },
  geannuleerd: { label: 'Geannuleerd', color: 'text-red-700', bgColor: 'bg-red-100' },
};

function isDeadlineUrgent(deadline: string | null): boolean {
  if (!deadline) return false;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diffDays = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays <= 7 && diffDays >= 0;
}

function isDeadlinePassed(deadline: string | null): boolean {
  if (!deadline) return false;
  return new Date(deadline) < new Date();
}

type SortField = 'deadline' | 'employee' | 'status' | 'type';
type SortDirection = 'asc' | 'desc';

export function AllAgreementsSection() {
  const { data, isLoading, error } = useAllOrganizationAgreements();
  const [selectedAgreement, setSelectedAgreement] = useState<ScoreAgreement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('deadline');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Alle Openstaande Afspraken</h3>
        </div>
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-500">Laden...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Alle Openstaande Afspraken</h3>
        </div>
        <div className="p-6">
          <p className="text-red-600 text-sm">Er is een fout opgetreden bij het laden van afspraken.</p>
        </div>
      </div>
    );
  }

  const agreements = data?.data || [];

  // Filter agreements (accent-insensitive)
  let filteredAgreements = agreements.filter((agreement) => {
    // Search filter
    if (searchQuery) {
      const matchesSearchFilter =
        matchesSearch(agreement.beschrijving, searchQuery) ||
        matchesSearch(agreement.employeeName ?? '', searchQuery) ||
        matchesSearch(agreement.agreementTypeName ?? '', searchQuery);
      if (!matchesSearchFilter) return false;
    }

    // Status filter
    if (statusFilter !== 'all' && agreement.status !== statusFilter) {
      return false;
    }

    return true;
  });

  // Sort agreements
  filteredAgreements = [...filteredAgreements].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'deadline':
        if (a.deadline && !b.deadline) comparison = -1;
        else if (!a.deadline && b.deadline) comparison = 1;
        else if (a.deadline && b.deadline) {
          comparison = new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        }
        break;
      case 'employee':
        comparison = (a.employeeName || '').localeCompare(b.employeeName || '');
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'type':
        comparison = (a.agreementTypeName || '').localeCompare(b.agreementTypeName || '');
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <span className="text-gray-400 ml-1">↕</span>;
    }
    return <span className="text-purple-600 ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Calculate statistics
  const stats = {
    total: agreements.length,
    urgent: agreements.filter(a => isDeadlineUrgent(a.deadline) || isDeadlinePassed(a.deadline)).length,
    employeeAction: agreements.filter(a => a.isEmployeeActionHolder).length,
    managerAction: agreements.filter(a => !a.isEmployeeActionHolder).length,
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-purple-200 shadow-sm">
        <div className="px-6 py-4 border-b border-purple-200 bg-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
              <h3 className="text-lg font-medium text-purple-800">
                Alle Openstaande Afspraken
                <span className="ml-2 px-2 py-0.5 text-sm bg-purple-200 text-purple-800 rounded-full">
                  {filteredAgreements.length}
                </span>
              </h3>
            </div>
          </div>
          <p className="mt-1 text-sm text-purple-600">
            Overzicht van alle afspraken in de organisatie
          </p>

          {/* Statistics */}
          <div className="mt-3 flex gap-4 text-sm">
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
              Totaal: {stats.total}
            </span>
            {stats.urgent > 0 && (
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                ⚠️ Urgent: {stats.urgent}
              </span>
            )}
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">
              Medewerker actie: {stats.employeeAction}
            </span>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
              Manager actie: {stats.managerAction}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Zoek op beschrijving, medewerker, of type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Alle</option>
              <option value="concept">Concept</option>
              <option value="definitief">Definitief</option>
            </select>
          </div>
        </div>

        {/* Table Header */}
        <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-2 bg-gray-100 text-xs font-medium text-gray-500 uppercase">
          <button
            onClick={() => handleSort('employee')}
            className="col-span-3 text-left flex items-center hover:text-gray-700"
          >
            Medewerker <SortIcon field="employee" />
          </button>
          <button
            onClick={() => handleSort('type')}
            className="col-span-2 text-left flex items-center hover:text-gray-700"
          >
            Type <SortIcon field="type" />
          </button>
          <div className="col-span-3 text-left">Beschrijving</div>
          <button
            onClick={() => handleSort('deadline')}
            className="col-span-2 text-left flex items-center hover:text-gray-700"
          >
            Deadline <SortIcon field="deadline" />
          </button>
          <button
            onClick={() => handleSort('status')}
            className="col-span-2 text-left flex items-center hover:text-gray-700"
          >
            Status <SortIcon field="status" />
          </button>
        </div>

        {/* Agreements List */}
        <div className="divide-y divide-gray-100">
          {filteredAgreements.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchQuery || statusFilter !== 'all'
                ? 'Geen afspraken gevonden met deze filters.'
                : 'Geen openstaande afspraken.'}
            </div>
          ) : (
            filteredAgreements.map((agreement) => {
              const statusConfig = STATUS_LABELS[agreement.status] ?? STATUS_LABELS.concept!;
              const urgent = isDeadlineUrgent(agreement.deadline);
              const passed = isDeadlinePassed(agreement.deadline);

              return (
                <div
                  key={agreement.id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedAgreement(agreement)}
                >
                  {/* Desktop view */}
                  <div className="hidden md:grid md:grid-cols-12 gap-4 items-center">
                    <div className="col-span-3">
                      <p className="text-sm font-medium text-gray-900">{agreement.employeeName || 'Onbekend'}</p>
                      {agreement.isEmployeeActionHolder && (
                        <span className="text-xs text-amber-600">Medewerker actiehouder</span>
                      )}
                    </div>
                    <div className="col-span-2">
                      {agreement.agreementTypeName && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                          {agreement.agreementTypeIcon} {agreement.agreementTypeName}
                        </span>
                      )}
                    </div>
                    <div className="col-span-3">
                      <p className="text-sm text-gray-700 line-clamp-2">{agreement.beschrijving}</p>
                    </div>
                    <div className="col-span-2">
                      {agreement.deadline ? (
                        <span className={`text-sm ${passed ? 'text-red-600 font-medium' : urgent ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                          {passed ? '⚠️ ' : urgent ? '⏰ ' : ''}
                          {new Date(agreement.deadline).toLocaleDateString('nl-NL')}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Mobile view */}
                  <div className="md:hidden">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">{agreement.employeeName || 'Onbekend'}</span>
                          {agreement.agreementTypeName && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                              {agreement.agreementTypeIcon} {agreement.agreementTypeName}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2 mb-1">{agreement.beschrijving}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {agreement.isEmployeeActionHolder && (
                            <span className="text-amber-600">Medewerker actiehouder</span>
                          )}
                          {agreement.deadline && (
                            <span className={passed ? 'text-red-600' : urgent ? 'text-orange-600' : ''}>
                              {passed ? '⚠️ ' : urgent ? '⏰ ' : ''}
                              Deadline: {new Date(agreement.deadline).toLocaleDateString('nl-NL')}
                            </span>
                          )}
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {selectedAgreement && (
        <AgreementDetailModal
          agreementId={selectedAgreement.id}
          onClose={() => setSelectedAgreement(null)}
          canEdit={true}
        />
      )}
    </>
  );
}
